import { useEffect, useMemo } from 'react';
import type { FlagsVariant } from 'mixpanel-browser';
import { useShallow } from 'zustand/react/shallow';

import type { FEATURE } from '~/common/enums/feature.enum';

import type { TreatmentWithConfig } from '../types';

import mixpanelFlagsClient from './mixpanelClient';
import { isSettledStatus, useMixpanelFlagsStore } from './state/mixpanelFlags.store';
import { toTreatment } from './toTreatment';

export const useMixpanelTreatments = <F extends FEATURE>(flags: F[]) => {
  const status = useMixpanelFlagsStore((state) => state.status);
  const variants = useMixpanelFlagsStore(
    useShallow((state) => Object.fromEntries(flags.map((flag) => [flag, state.variants[flag]])))
  ) as Partial<Record<F, FlagsVariant>>;
  const isSettled = isSettledStatus(status);

  const treatments = useMemo(
    () =>
      flags.reduce(
        (acc, flag) => {
          acc[flag] = isSettled ? toTreatment(flag, variants[flag]) : { treatment: 'control' };
          return acc;
        },
        {} as Record<F, TreatmentWithConfig>
      ),
    [flags, variants, isSettled]
  );

  useEffect(() => {
    flags.forEach((flag) => {
      if (variants[flag]?.is_experiment_active) mixpanelFlagsClient.trackExposure(flag);
    });
  }, [flags, variants]);

  return { isReady: status === 'ready', isSettled, treatments };
};
